import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { isLockedOnPath } from '../../lib/arcade'
import { fetchPath } from '../../services/arcade'
import StatusBanner from '../common/StatusBanner'

/**
 * The banner alone, without the fetch — so the page's smoke test can render
 * it from a prop, and so the one decision it makes (say it or not) is the
 * caller's.
 */
export function LockedBanner() {
  const { t } = useTranslation('arcade')
  return (
    <StatusBanner
      tone="warn"
      icon="warning"
      className="mb-4"
      action={
        <Link to="/lessons" className="shrink-0 text-[11.5px] font-bold text-warn-ink underline">
          {t('banner.backToPath')}
        </Link>
      }
    >
      {t('banner.locked')}
    </StatusBanner>
  )
}

/**
 * A direct `/lesson/:id` link to a lesson the path still keeps shut (V7-w,
 * decision Q13).
 *
 * Q13's rule is that the lesson **opens**: locks on the path are advisory,
 * `lessons_detail` and `attempts_submit` only refuse while
 * `arcade.enforceLockOnSubmit` is on, and a shared link, a notification or
 * a bookmark must keep working. So this neither redirects nor disables
 * anything — it says «این درس هنوز در مسیرت باز نشده» above the lesson and
 * offers the way back to the path.
 *
 * It asks `arcade_path` itself rather than taking the answer from the page
 * that linked here, because the interesting case is exactly the one with no
 * such page. Every failure is silent: a 403 from the premium gate, an
 * offline phone, a lesson that sits in no stage at all — all of them mean
 * "nothing to say here", and none of them should put an error on a lesson
 * that is otherwise working.
 */
export default function ArcadeLockBanner({ lessonId }) {
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLocked(false)
    if (!lessonId) return undefined
    fetchPath()
      .then((path) => {
        if (!cancelled) setLocked(isLockedOnPath(path, lessonId))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [lessonId])

  if (!locked) return null
  return <LockedBanner />
}
